/**
 * User display preferences (DESIGN.md §4): theme and density.
 *
 * Effective value per field: the user's own choice → instance default (Settings → Branding) →
 * built-in default. Only explicit choices are stored (localStorage for now; the platform API will
 * own them later so they follow the user across devices). Every storage access is guarded.
 */
import { DENSITIES, THEMES } from '@platform/branding';
import { readCachedBranding } from './brand';

export { DENSITIES, THEMES };
export type Theme = (typeof THEMES)[number];
export type Density = (typeof DENSITIES)[number];

export interface Preferences {
  theme: Theme;
  density: Density;
}

export const DEFAULT_PREFERENCES: Preferences = { theme: 'system', density: 'compact' };

const STORAGE_KEY = 'platform.preferences';

function isTheme(value: unknown): value is Theme {
  return typeof value === 'string' && (THEMES as readonly string[]).includes(value);
}

function isDensity(value: unknown): value is Density {
  return typeof value === 'string' && (DENSITIES as readonly string[]).includes(value);
}

/** The user's explicit choices (fields they never chose are missing). */
export function readStoredPreferences(): Partial<Preferences> {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Partial<Record<keyof Preferences, unknown>>;
    return {
      ...(isTheme(parsed.theme) ? { theme: parsed.theme } : {}),
      ...(isDensity(parsed.density) ? { density: parsed.density } : {}),
    };
  } catch {
    return {};
  }
}

export function writeStoredPreferences(preferences: Partial<Preferences>): void {
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(preferences));
  } catch {
    // Not persisted; still applied for this page view.
  }
}

/** Effective preferences: stored choice → instance default → built-in default. */
export function effectivePreferences(stored: Partial<Preferences>, instanceDefaults: Partial<Preferences> = {}): Preferences {
  return { ...DEFAULT_PREFERENCES, ...instanceDefaults, ...stored };
}

export function systemPrefersDark(): boolean {
  return globalThis.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
}

/** Resolves `system` to the effective theme. */
export function resolveTheme(theme: Theme): 'light' | 'dark' {
  if (theme === 'system') return systemPrefersDark() ? 'dark' : 'light';
  return theme;
}

/** Applies preferences to <html>: `.dark` class and `data-density` attribute. */
export function applyPreferences(preferences: Preferences, root: HTMLElement = document.documentElement): void {
  root.classList.toggle('dark', resolveTheme(preferences.theme) === 'dark');
  root.dataset.density = preferences.density;
}

/** Instance defaults from the last branding seen (cached), for the first paint. */
export function cachedInstanceDefaults(): Partial<Preferences> {
  const settings = readCachedBranding()?.settings;
  return settings ? { theme: settings.defaultTheme, density: settings.defaultDensity } : {};
}

/**
 * Call once before the first React render so the page never flashes the wrong theme.
 * Returns the preferences that were applied.
 */
export function initPreferences(): Preferences {
  const preferences = effectivePreferences(readStoredPreferences(), cachedInstanceDefaults());
  applyPreferences(preferences);
  return preferences;
}
